import {
  Box,
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
  InputAdornment,
  Pagination,
} from '@mui/material';
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import Fuse from 'fuse.js';
import Badge from '@material-ui/core/Badge';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import Paper from '@mui/material/Paper';
import LocationOn from '@mui/icons-material/LocationOn';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { isBefore } from 'date-fns';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import config from '../../../../config';
import RequestService from '../../../../services/RequestService';
import WushCard from '../../../../components/WushCard';
import Loader from '../../../../components/loader/Loader';
import ReservationCard from '../../../../components/cards/ReservationCard';
import DeliveryMap from '../deliveries/DeliveryMap';

import DateFilter from '../../../../components/filters/DateFilter';
import ZoneFilter from '../../../../components/filters/ZoneFilter';
import ItemCategoryFilter from '../../../../components/filters/ItemCategoryFilter';

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: grid * 1,
  borderRadius: '10px',

  // change background colour if dragging
  background: isDragging ? 'white' : 'white',
  transform: isDragging ? 'rotate(10deg)' : 'rotate(10deg)',
  boxShadow: isDragging ? '8px 8px 10px darkgrey' : '',

  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? 'lightgrey' : 'white',
  padding: grid,
  width: '100%',
});

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const Pickups = ({ clientStore, itemCategoryStore, locationZoneStore }) => {
  const [reservationTypeFilter] = React.useState([]);
  const [paymentStatusFilter] = React.useState([]);
  const [selectedPickups, setSelectedPickups] = React.useState([]);
  const [search, setSearch] = React.useState();
  const [pickups, setPickups] = React.useState(null);
  const [showMap, setShowMap] = useState(false);
  const handleShowMap = () => {
    setShowMap(!showMap);
  };

  const [itemCategoryFilter, setItemCategoryFilter] = useState([]);
  const [zoneFilter, setZoneFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState([]);
  const [dateData, setDateData] = useState([]);

  const pickupStyle = true;

  // pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Item Category Filter Button Function
  const handleItemCategoryFilterCheck = (filterValue, checked) => {
    if (checked) {
      setItemCategoryFilter(itemCategoryFilter.concat([filterValue]));
    } else {
      setItemCategoryFilter(itemCategoryFilter.filter((e) => e !== filterValue));
    }
  };

  //  Zone Filter Button Function
  const handleZoneFilterCheck = (filterValue, checked) => {
    if (checked) {
      setZoneFilter(zoneFilter.concat([filterValue]));
    } else {
      setZoneFilter(zoneFilter.filter((e) => e !== filterValue));
    }
  };

  // Date Filter Button Function
  const handleDateFilterCheck = (filterValue, checked, dateValue) => {
    if (checked) {
      setDateFilter(dateFilter.pop());
      setDateFilter(dateFilter.concat([filterValue]));
      setDateData(dateData.pop());
      setDateData(dateData.concat([dateValue]));
    } else {
      setDateFilter(dateFilter.filter((e) => e !== filterValue));
      setDateData(dateData.filter((e) => e !== filterValue));
    }
  };

  const handleDateFilterLogic = (pickupDate) => {
    const newDate = new Date(pickupDate);

    if (isBefore(newDate, dateData[0])) {
      return true;
    }
    return false;
  };

  const history = useHistory();

  const fuseOptions = {
    keys: [
      { name: 'id', weight: 1.0 },
      'reservationId',
      'reservation.propertyName',
      'reservation.customerName',
    ],
  };

  clientStore.getClients();
  itemCategoryStore.getItemCategories();
  locationZoneStore.getLocationZones();

  if (!pickups) {
    RequestService.get(`${config.backendUrl}/pickups`, (response) => {
      setPickups(response.data);
    });
  }

  if (!pickups) return <Loader />;

  if (!itemCategoryStore.itemCategoriesPreLoaded) return <LinearProgress />;

  const fusePickups = new Fuse(pickups, fuseOptions);

  const filterPickups = () => pickups.filter(
    (delivery) => reservationTypeFilter.includes(delivery.reservation.reservationType)
        || delivery.reservation.itemCategoryNames
          .some((itemCategoryName) => itemCategoryFilter.includes(itemCategoryName))
        || paymentStatusFilter.includes(delivery.reservation.paid ? 'paid' : 'unpaid')
        || zoneFilter.includes(delivery.reservation.propertyZoneName)
        || handleDateFilterLogic(delivery.pickupDate),
  );

  const handleSearch = (value) => {
    setSearch(value);
  };

  const filteredPickups = () => {
    if (
      reservationTypeFilter.length > 0
      || itemCategoryFilter.length > 0
      || paymentStatusFilter.length > 0
      || zoneFilter.length > 0
      || dateFilter.length > 0
    ) {
      return filterPickups();
    }
    if (search) {
      return fusePickups.search(search).map((item) => item.item);
    }

    return pickups;
  };

  const createDeliveryRoute = () => {
    RequestService.post(
      `${config.backendUrl}/delivery-routes`,
      {
        deliveryRoute: {
          deliveryIds: selectedPickups.map((delivery) => delivery.id),
        },
      },
      (response) => {
        history.push(`/reservation-management/delivery-routes/${response.data.id}`);
      },
    );
  };

  const handleDrop = (result) => {
    if (
      !result.destination
      || (result.source.droppableId === 'full-list' && result.destination.droppableId === 'full-list')
    ) {
      return;
    }

    if (result.source.droppableId === 'route' && result.destination.droppableId === 'route') {
      const reordered = reorder(selectedPickups, result.source.index, result.destination.index);

      setSelectedPickups(reordered);
    } else if (result.destination.droppableId === 'route') {
      const updatedSelected = selectedPickups.concat(
        pickups.filter((e) => parseInt(e.id, 10) === parseInt(result.draggableId, 10))[0],
      );
      setSelectedPickups(updatedSelected);
      setPickups(pickups.filter((e) => parseInt(e.id, 10) !== parseInt(result.draggableId, 10)));
    } else if (result.source.droppableId === 'route') {
      setSelectedPickups(
        selectedPickups.filter((e) => parseInt(e.id, 10) !== parseInt(result.draggableId, 10)),
      );
      setPickups(
        pickups.concat(
          selectedPickups.filter((e) => parseInt(e.id, 10) === parseInt(result.draggableId, 10))[0],
        ),
      );
    }
  };

  const handleAddToRoute = (delivery) => {
    const updatedSelected = selectedPickups.concat(
      pickups.filter((e) => parseInt(e.id, 10) === parseInt(delivery.id, 10))[0],
    );
    setSelectedPickups(updatedSelected);
    setPickups(pickups.filter((e) => parseInt(e.id, 10) !== parseInt(delivery.id, 10)));
  };

  function groupReservationItems(selectedPickupsGroups) {
    const groupedItems = {};
    let totalItems = 0;
    const newSelectedPickups = selectedPickupsGroups.map((delivery) => {
      const { reservation } = delivery;
      const items = reservation.reservationItems;
      const deliveryItems = {};

      items.forEach((item) => {
        const { itemName } = item;
        const itemQuantity = item.reservedQuantity;

        if (deliveryItems[itemName]) {
          deliveryItems[itemName] += itemQuantity;
        } else {
          deliveryItems[itemName] = itemQuantity;
        }

        if (groupedItems[itemName]) {
          groupedItems[itemName] += itemQuantity;
        } else {
          groupedItems[itemName] = itemQuantity;
        }

        totalItems += itemQuantity;
      });

      return { ...delivery, deliveryItems };
    });

    return { groupedItems, totalItems, selectedPickupsGroups: newSelectedPickups };
  }

  const { groupedItems, totalItems } = groupReservationItems(selectedPickups);

  // pagination logic and variables
  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };
  // set how many Pickups per screen
  const rowsPerPage = 20;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentPickups = filteredPickups().slice(startIndex, endIndex);

  return (
    <Box py={2} className="deliveries">
      <Grid container>
        <Grid item md={9} textAlign="left">
          <Box display="flex" justifyContent="space-between" mr={6} pb={3}>
            <Box className="filterBox" display="flex">
              <FilterAltOutlinedIcon />
              <Typography ml={1}>Filter by Order</Typography>
            </Box>

            {/* Date Filter Button */}
            <Box>
              <DateFilter
                dateFilter={dateFilter}
                handleDateFilterCheck={handleDateFilterCheck}
              />
            </Box>

            {/* Zone Filter Button */}
            <Box>
              <ZoneFilter
                zoneFilter={zoneFilter}
                handleZoneFilterCheck={handleZoneFilterCheck}
                locationZoneStore={locationZoneStore}
              />
            </Box>

            {/* Item Category Button */}
            <Box>
              <ItemCategoryFilter
                itemCategoryFilter={itemCategoryFilter}
                handleItemCategoryFilterCheck={handleItemCategoryFilterCheck}
                itemCategoryStore={itemCategoryStore}
              />
            </Box>
          </Box>

          <Box mb={0} display="flex" className="reservationBox">
            <Typography variant="h5">Pickups</Typography>
            <TextField
              className="searchBar"
              onChange={(e) => handleSearch(e.target.value)}
              value={search}
              label="Search"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <hr />
        </Grid>

        <Grid item md={3} textAlign="right">
          <Box mb={2}>
            <Button
              variant="contained"
              onClick={createDeliveryRoute}
              disabled={selectedPickups.length === 0}
            >
              Create Route
            </Button>
          </Box>
        </Grid>
        <DragDropContext onDragEnd={handleDrop}>
          <Grid item md={9} mt={-1}>
            <Droppable droppableId="full-list">
              {(providedUpper, snapshotUpper) => (
                <Box
                  {...providedUpper.droppableProps}
                  ref={providedUpper.innerRef}
                  display="flex"
                  flexWrap="wrap"
                  style={getListStyle(snapshotUpper.isDraggingOver)}
                >
                  {currentPickups.map((delivery, index) => (
                    <Draggable key={delivery.id} draggableId={`${delivery.id}`} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          width="25%"
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                          <ReservationCard
                            reservation={delivery.reservation}
                            addToRoute={() => handleAddToRoute(delivery)}
                            ispickup={pickupStyle}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                </Box>
              )}
            </Droppable>
            <Box mb={1} className="pagination">
              <Pagination
                count={Math.ceil(pickups.length / rowsPerPage)}
                page={currentPage}
                onChange={handleChangePage}
                showFirstButton
                showLastButton
              />
            </Box>
          </Grid>
          <Grid item md={3}>
            <Droppable droppableId="route">
              {(providedUpper, snapshotUpper) => (
                <Box border="1px solid lightblue" borderRadius="10px">
                  <Box
                    padding={1}
                    bgcolor="lightblue"
                    borderRadius="10px 10px 0px 0"
                    border="1px solid lightblue"
                  >
                    <strong>New Delivery Route</strong>
                  </Box>
                  <Box
                    {...providedUpper.droppableProps}
                    ref={providedUpper.innerRef}
                    style={getListStyle(snapshotUpper.isDraggingOver)}
                  >
                    {selectedPickups.length === 0 && (
                      <Box border="1px dashed grey" p={1}>
                        <Typography variant="body1">Drag cards here to create route</Typography>
                      </Box>
                    )}
                    {selectedPickups.map((delivery, index) => (
                      <Draggable key={delivery.id} draggableId={`${delivery.id}`} index={index}>
                        {(provided, snapshot) => (
                          <Box
                            width="100%"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                          >
                            <WushCard>
                              <Box display="flex" alignItems="center">
                                <Box mr={2}>
                                  <Badge badgeContent={index + 1} color="white">
                                    <LocationOn style={{ color: '#00ccff' }} />
                                  </Badge>
                                </Box>
                                <Box mr={2}>
                                  <Typography variant="subtitle2">
                                    <strong>{delivery.reservation.propertyName}</strong>
                                    <strong>{'  '}</strong>
                                    {delivery.reservation.propertyAddress}
                                  </Typography>
                                </Box>
                              </Box>
                            </WushCard>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {selectedPickups.length > 0 && (
                      <>
                        <Box textAlign="right" mb={1}>
                          <Button variant="contained" color="primary" onClick={handleShowMap}>
                            Show Map
                          </Button>
                        </Box>
                        {showMap && <DeliveryMap deliveries={selectedPickups} />}
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Droppable>
            <Paper
              sx={{
                padding: '8px',
                borderRadius: '20px',
                boxShadow:
                  '0px 0px 5px rgba(255, 255, 255, 2), 0px 0px 5px rgba(128, 128, 128, 0.5)',
                position: 'relative',
                bgcolor: 'grey.200',
              }}
            >
              <div
                style={{
                  top: '-10px',
                  left: '-10px',
                  right: '-10px',
                  bottom: '-10px',
                  background:
                    'linear-gradient(to bottom right, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3))',
                  borderRadius: '30px',
                  zIndex: '-1',
                  boxShadow: '0 0 10px rgba(255, 255, 255, 0.5) inset',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  className="progress-bar"
                >
                  <p>% Full</p>
                  <p>80% expand</p>
                </Box>
                <Box display="flex" flexDirection="row" p={0.5}>
                  <LocalShippingOutlinedIcon />
                  <Box sx={{ width: '100%' }} p={1}>
                    <LinearProgress />
                  </Box>
                </Box>

                {Object.keys(groupedItems).map((itemName) => (
                  <Box sx={{ marginBottom: '5px' }} className="progress-bar route-item-listout">
                    <p>
                      {groupedItems[itemName]}
                      {'x      '}
                      {itemName}
                    </p>
                  </Box>
                ))}
                <Typography>
                  Total Items:
                  {totalItems}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </DragDropContext>
      </Grid>
    </Box>
  );
};

Pickups.propTypes = {
  clientStore: PropTypes.any,
};

export default observer(Pickups);
